import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { TeamPlayer } from "../../../types";
import type { MatchEvent } from "../../../store/useMatchLogStore";
import { formatPlayerName } from "../../../lib/nameFormat";
import type { Match, Player } from "../types";
import {
  fetchMatchLineupSnapshot,
  fetchTeamRoster,
  normalizeLineup,
  patchMatchLineups,
  type LineupPlayerSelection,
} from "../lib/lineupSnapshot";

/** Adapts a full-club-roster `TeamPlayer` (squad, not yet in the lineup)
 * into the cockpit's own `Player` shape so it can render in the same
 * `sub-on-*` chip list as an existing lineup substitute. */
const teamPlayerToLoggerPlayer = (player: TeamPlayer): Player => ({
  id: player.player_id,
  full_name: formatPlayerName(player.player_name || player.player_id),
  // `?? 0` widens for TeamPlayer's now-nullable jersey_number (EPIC-95 T5) so
  // `Player.jersey_number` can stay a required number everywhere else in the
  // cockpit. `0` is a deliberate "missing" sentinel here, not a fallback to
  // ignore: `SubstitutionFlow.tsx`'s `formatJerseyBadge`/`byJerseyAscending`
  // render it as "—" and sort it last, and `onSubmit` below blocks a squad
  // pick with this sentinel before it ever reaches the server.
  jersey_number: player.jersey_number ?? 0,
  position: player.position,
  is_starter: false,
});

type TeamSide = "home" | "away";

interface TeamRosterState {
  roster: TeamPlayer[];
  lineup: LineupPlayerSelection[];
}

const EMPTY_ROSTER_STATE: TeamRosterState = { roster: [], lineup: [] };

interface UseCockpitSubstitutionFlowParams {
  match: Match | null;
  substitutionTeam: "home" | "away";
  onFieldIds: { home: Set<string>; away: Set<string> };
  showSubstitutionFlow: boolean;
  cockpitLocked: boolean;
  expelledPlayerIds: Set<string>;
  // PMA-COCK-086 broadcast-cap value (formatMatchClock(broadcastClock.mainSeconds)
  // in CockpitContext.tsx) — NOT the raw accumulating global clock. Substitutions
  // were stamped off the raw clock until this fix; in the second half the raw
  // clock carries first-half stoppage (period_timestamps["2"].global_start_seconds)
  // forward, which the broadcast clock deliberately does not (see PMA-COCK-017
  // changelog / PMA-COCK-086 AC6). Named distinctly from `matchClock`
  // (useActionFlow.ts's equivalent param) only to avoid any ambiguity with this
  // file's own future param choices — same value, same semantics.
  broadcastMatchClock: string;
  operatorPeriod: number;
  applyOnFieldChange: (
    team: "home" | "away",
    offId?: string,
    onId?: string,
  ) => void;
  sendEvent: (eventData: Omit<MatchEvent, "match_id" | "timestamp">) => void;
  setShowSubstitutionFlow: (open: boolean) => void;
  showTimedToast: (message: string, timeoutMs?: number) => void;
  t: (key: string, fallback: string) => string;
  /** PMA-COCK-171: needed to refresh the cockpit's own match state after a
   * squad pick's bench-PATCH succeeds, so `availablePlayers` includes the
   * newly-added substitute before the Substitution event dispatches. */
  fetchMatch: () => Promise<void>;
}

interface UseCockpitSubstitutionFlowResult {
  modalTeam: Match["home_team"] | Match["away_team"] | null;
  availablePlayers: Match["home_team"]["players"];
  /** PMA-COCK-171: roster players not currently in the lineup at all — step
   * 2's "Not in the match squad" section, below the bench section. */
  squadPlayers: Player[];
  onField: Set<string>;
  onSubmit: (
    playerOffId: string,
    playerOnId: string,
    isConcussion: boolean,
  ) => void;
  onCancel: () => void;
  /** True while a squad pick's bench-PATCH is in flight — the confirm step
   * shows a spinner and the submit button stays disabled. */
  isSubmittingSquadPlayer: boolean;
  /** The server's error detail when a squad pick's bench-PATCH failed — the
   * Substitution event is never sent in that case. Cleared on every new
   * selection and on modal close. */
  squadSubmitError: string | null;
}

const GENERIC_SQUAD_PATCH_ERROR =
  "Unable to update the lineup. Please try again.";

export const useCockpitSubstitutionFlow = ({
  match,
  substitutionTeam,
  onFieldIds,
  showSubstitutionFlow,
  cockpitLocked,
  expelledPlayerIds,
  broadcastMatchClock,
  operatorPeriod,
  applyOnFieldChange,
  sendEvent,
  setShowSubstitutionFlow,
  showTimedToast,
  t,
  fetchMatch,
}: UseCockpitSubstitutionFlowParams): UseCockpitSubstitutionFlowResult => {
  const modalTeam = useMemo(() => {
    if (!match) return null;
    return substitutionTeam === "home" ? match.home_team : match.away_team;
  }, [match, substitutionTeam]);

  const availablePlayers = useMemo(() => {
    if (!match) return [] as Match["home_team"]["players"];
    return substitutionTeam === "home"
      ? match.home_team.players
      : match.away_team.players;
  }, [match, substitutionTeam]);

  // PMA-COCK-171: "the rest of the squad" — a single load point for both
  // teams' full club roster plus the raw match-lineup snapshot, fetched
  // once per (matchId, homeTeamId, awayTeamId) tuple (never on every
  // render/keystroke). Reuses the exact helpers `PreMatchLineupPanel.tsx`
  // uses for its own roster/lineup fetch (`lib/lineupSnapshot.ts`) so the
  // two surfaces cannot silently drift apart.
  const [homeRosterState, setHomeRosterState] =
    useState<TeamRosterState>(EMPTY_ROSTER_STATE);
  const [awayRosterState, setAwayRosterState] =
    useState<TeamRosterState>(EMPTY_ROSTER_STATE);
  const [squadSnapshotId, setSquadSnapshotId] = useState<string | null>(null);
  const squadStateRef = useRef({
    home: homeRosterState,
    away: awayRosterState,
  });
  squadStateRef.current = { home: homeRosterState, away: awayRosterState };

  const matchId = match?.id;
  const homeTeamId = match?.home_team.team_id || match?.home_team.id;
  const awayTeamId = match?.away_team.team_id || match?.away_team.id;
  const loadedSquadKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!matchId) return;
    const key = `${matchId}:${homeTeamId ?? ""}:${awayTeamId ?? ""}`;
    if (loadedSquadKeyRef.current === key) return;
    loadedSquadKeyRef.current = key;
    Promise.all([
      homeTeamId ? fetchTeamRoster(homeTeamId) : Promise.resolve([]),
      awayTeamId ? fetchTeamRoster(awayTeamId) : Promise.resolve([]),
      fetchMatchLineupSnapshot(matchId),
    ])
      .then(([homeRoster, awayRoster, snapshot]) => {
        setHomeRosterState({
          roster: homeRoster,
          lineup: normalizeLineup(snapshot.home_team?.lineup),
        });
        setAwayRosterState({
          roster: awayRoster,
          lineup: normalizeLineup(snapshot.away_team?.lineup),
        });
        setSquadSnapshotId(snapshot._id ?? null);
      })
      .catch(() => {
        // Best-effort: an operator whose squad list never loads still sees
        // the (unaffected) bench list and, worst case, the empty-state
        // message below it — never a broken modal.
        loadedSquadKeyRef.current = null;
      });
  }, [matchId, homeTeamId, awayTeamId]);

  const squadPlayersForTeam = useCallback((team: TeamSide): TeamPlayer[] => {
    const { roster, lineup } =
      team === "home" ? squadStateRef.current.home : squadStateRef.current.away;
    const lineupIds = new Set(lineup.map((p) => p.player_id));
    return roster.filter((p) => !lineupIds.has(p.player_id));
  }, []);

  // PMA-COCK-171: roster players with no lineup entry at all for this team —
  // "Fuera de la convocatoria" in step 2, below the bench section. Only
  // computed while the modal is open; cheap either way (a club roster is a
  // few dozen players at most), but there's no reason to do it while closed.
  const squadPlayers = useMemo(() => {
    if (!match || !showSubstitutionFlow) return [] as Player[];
    return squadPlayersForTeam(substitutionTeam).map(teamPlayerToLoggerPlayer);
  }, [
    match,
    showSubstitutionFlow,
    substitutionTeam,
    squadPlayersForTeam,
    homeRosterState,
    awayRosterState,
  ]);

  const onField = useMemo(
    () => (substitutionTeam === "home" ? onFieldIds.home : onFieldIds.away),
    [substitutionTeam, onFieldIds.home, onFieldIds.away],
  );

  const [onFieldSnapshot, setOnFieldSnapshot] = useState<Set<string>>(
    new Set(onField),
  );
  const [isSubmittingSquadPlayer, setIsSubmittingSquadPlayer] = useState(false);
  const [squadSubmitError, setSquadSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!showSubstitutionFlow) {
      return;
    }
    setOnFieldSnapshot(new Set(onField));
  }, [showSubstitutionFlow, onField]);

  useEffect(() => {
    if (showSubstitutionFlow) return;
    setIsSubmittingSquadPlayer(false);
    setSquadSubmitError(null);
  }, [showSubstitutionFlow]);

  // PATCHes `player` onto `team`'s bench (bench-only lineup edit — the
  // server accepts this while the match is live, PMA-MATCH-028), then
  // refreshes the cockpit's own match state so `availablePlayers` includes
  // them before the caller dispatches the Substitution event.
  const addPlayerToBench = useCallback(
    async (
      team: TeamSide,
      player: TeamPlayer,
    ): Promise<{ ok: true } | { ok: false; error: string }> => {
      if (!squadSnapshotId) {
        return { ok: false, error: GENERIC_SQUAD_PATCH_ERROR };
      }
      const { home, away } = squadStateRef.current;
      const currentLineup = (team === "home" ? home : away).lineup;
      const nextLineup: LineupPlayerSelection[] = [
        ...currentLineup,
        {
          player_id: player.player_id,
          player_name: player.player_name || player.player_id,
          position: player.position,
          // Type-safety fallback only — `onSubmit`'s caller already blocks a
          // null-jersey squad player before this function is ever invoked
          // (EPIC-95 T5), so `?? 0` should never actually fire here.
          jersey_number: player.jersey_number ?? 0,
          is_starter: false,
        },
      ];
      const nextHome = team === "home" ? nextLineup : home.lineup;
      const nextAway = team === "away" ? nextLineup : away.lineup;
      try {
        await patchMatchLineups(squadSnapshotId, nextHome, nextAway);
        setHomeRosterState((prev) => ({ ...prev, lineup: nextHome }));
        setAwayRosterState((prev) => ({ ...prev, lineup: nextAway }));
        await fetchMatch();
        return { ok: true };
      } catch (err: any) {
        return {
          ok: false,
          error:
            err?.response?.data?.detail ||
            err?.message ||
            GENERIC_SQUAD_PATCH_ERROR,
        };
      }
    },
    [squadSnapshotId, fetchMatch],
  );

  const onSubmit = (
    playerOffId: string,
    playerOnId: string,
    isConcussion: boolean,
  ) => {
    if (!match || cockpitLocked) return;

    if (
      expelledPlayerIds.has(playerOffId) ||
      expelledPlayerIds.has(playerOnId)
    ) {
      showTimedToast(
        t(
          "substitutionExpelledBlocked",
          "Expelled players cannot be substituted.",
        ),
      );
      return;
    }

    const team =
      substitutionTeam === "home" ? match.home_team : match.away_team;

    const dispatchSubstitution = () => {
      const eventData: Omit<MatchEvent, "match_id" | "timestamp"> = {
        match_clock: broadcastMatchClock,
        period: operatorPeriod,
        team_id: team.id,
        player_id: playerOffId,
        type: "Substitution",
        data: {
          player_off_id: playerOffId,
          player_on_id: playerOnId,
          is_concussion: isConcussion,
        },
      };

      applyOnFieldChange(substitutionTeam, playerOffId, playerOnId);
      sendEvent(eventData);
      setShowSubstitutionFlow(false);
      setSquadSubmitError(null);
    };

    // A "squad pick" is a player with no lineup entry at all — user decision
    // 2026-09-07: add them to the bench (bench-only lineup PATCH) first, and
    // only send the Substitution event once that succeeds. A bench pick
    // (already in `availablePlayers`) is unchanged from before.
    const isSquadPick = !availablePlayers.some((p) => p.id === playerOnId);
    if (!isSquadPick) {
      dispatchSubstitution();
      return;
    }

    const squadPlayer = squadPlayersForTeam(substitutionTeam).find(
      (p) => p.player_id === playerOnId,
    );
    if (!squadPlayer) {
      setSquadSubmitError(
        t(
          "substitution.squadAddError",
          "Unable to add this player to the bench. Please try again.",
        ),
      );
      return;
    }

    // EPIC-95 T5: a roster-import squad player can have no jersey number yet
    // (`TeamPlayer.jersey_number` is nullable since be-jersey's contract
    // change). Block BEFORE the PATCH — `addPlayerToBench` would otherwise
    // silently send the `?? 0` sentinel and record a fake jersey number in
    // the live match lineup, since `MatchPlayer.jersey_number` stays a
    // required int on the backend and would accept `0` without complaint.
    // There is no live backend message for this exact path (the friendly
    // per-player 400 lives in `build_match_lineup`, confirmed dead code with
    // no live callers), so the cockpit supplies its own, mirroring the
    // save-time wording MatchesManager.tsx / PreMatchLineupPanel.tsx use for
    // `findMissingJerseyNumberPlayers` (`lineupSaveMissingJerseyNumbers`).
    if (
      squadPlayer.jersey_number === undefined ||
      squadPlayer.jersey_number === null
    ) {
      setSquadSubmitError(
        t(
          "substitution.squadPlayerMissingJerseyNumber",
          "{{name}} needs a jersey number before joining the lineup",
        ).replace("{{name}}", squadPlayer.player_name || squadPlayer.player_id),
      );
      return;
    }

    setSquadSubmitError(null);
    setIsSubmittingSquadPlayer(true);
    addPlayerToBench(substitutionTeam, squadPlayer).then((result) => {
      setIsSubmittingSquadPlayer(false);
      if (result.ok) {
        dispatchSubstitution();
      } else {
        setSquadSubmitError(result.error);
      }
    });
  };

  const onCancel = () => {
    setShowSubstitutionFlow(false);
  };

  return {
    modalTeam,
    availablePlayers,
    squadPlayers,
    onField: showSubstitutionFlow ? onFieldSnapshot : onField,
    onSubmit,
    onCancel,
    isSubmittingSquadPlayer,
    squadSubmitError,
  };
};
